import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { CustomInstruction } from '@services/custom-instructions.service';
import { PodcastService } from '@services/sessions/podcast.service';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { finalize, of, Subscription } from 'rxjs';

type BrainstormConceptFormGroup = FormGroup<{
  concept: FormControl<string>;
  selected: FormControl<boolean>;
  previous: FormControl<boolean>;
}>;

type BrainstormConceptsFormArray = FormArray<BrainstormConceptFormGroup>;

interface FormGroupType {
  concepts: FormArray<BrainstormConceptFormGroup>;
}

@Component({
  selector: 'app-brainstorm-concepts',
  standalone: true,
  imports: [ReactiveFormsModule, ButtonModule, AccordionModule],
  templateUrl: './brainstorm-concepts.component.html',
  styleUrl: './brainstorm-concepts.component.scss',
})
export class BrainstormConceptsComponent implements OnInit {
  @Input() subject: string;
  @Input() customInstruction: CustomInstruction;

  form: FormGroup<FormGroupType>;
  isBrainstorming = false;

  get mappedBrainstormConcepts() {
    return (
      this.form.get('concepts') as BrainstormConceptsFormArray
    ).controls.map((control, index) => ({ control, index }));
  }

  get currentConcepts() {
    return this.mappedBrainstormConcepts.filter(i => !i.control.value.previous);
  }

  get previousConcepts() {
    return this.mappedBrainstormConcepts.filter(i => i.control.value.previous);
  }

  private formBuilder = inject(FormBuilder);
  private podcastService = inject(PodcastService);
  private destroyRef = inject(DestroyRef);
  private regenerateReq: Subscription;

  ngOnInit() {
    this.createForm();
  }

  createForm() {
    this.form = this.formBuilder.group({
      concepts: this.formBuilder.array<BrainstormConceptFormGroup>([]),
    });
  }

  regenerate() {
    this.regenerateReq = this.brainstormConcepts(true).subscribe(concepts =>
      this.setBrainstormConcepts(concepts, false),
    );
  }

  onBrainstormConcepts() {
    this.brainstormConcepts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(concepts => this.setBrainstormConcepts(concepts));
  }

  brainstormConcepts(isRegenerate = false) {
    const concepts = this.form.get('concepts').value;

    this.stopRegeneratingBrainstormConcepts();

    if (!this.subject) {
      return of([]);
    }

    this.isBrainstorming = true;

    return this.podcastService
      .brainstormConcepts({
        topic: this.subject,
        context: this.customInstruction.content,
        alreadyGeneratedConcepts: isRegenerate
          ? concepts.map(v => v.concept)
          : undefined,
      })
      .pipe(finalize(() => (this.isBrainstorming = false)));
  }

  private setBrainstormConcepts(concepts: string[], shouldClear = true) {
    const brainstormConceptsFormArray = this.form.get(
      'concepts',
    ) as BrainstormConceptsFormArray;

    if (shouldClear) {
      brainstormConceptsFormArray.clear();
    } else {
      for (const control of brainstormConceptsFormArray.controls) {
        control.patchValue({ selected: false, previous: true });
      }
    }

    for (const concept of concepts) {
      brainstormConceptsFormArray.push(
        this.formBuilder.group({
          concept: this.formBuilder.control(concept),
          selected: this.formBuilder.control(false),
          previous: this.formBuilder.control(false),
        }),
      );
    }
  }

  private stopRegeneratingBrainstormConcepts() {
    if (this.regenerateReq) {
      this.regenerateReq.unsubscribe();
      this.regenerateReq = null;
      this.isBrainstorming = false;
    }
  }
}
